/**
 * "Send me back where I was" for the auth forms.
 *
 * Before this, every gate in the app — the /account and /admin bounces, the
 * "log in to use the Query Matcher" 401 prompt, the watchlist and feed
 * prompts, the header button — dropped the visitor's destination on the floor
 * and both auth forms hard-navigated to /dashboard. Someone deep-linked to a
 * ticker page who logged in to see the full list got the dashboard instead of
 * the page they were reading, with no way back but the browser history.
 *
 * The destination rides in a `?next=` query param rather than router location
 * state, so it survives a full page load (the /login link on the
 * server-rendered /stocks pages, a bookmark, an OG share link).
 */

import { useCallback } from "react";
import { To, useLocation } from "react-router-dom";

export const DEFAULT_AFTER_AUTH = "/dashboard";

const NEXT_PARAM = "next";

/** Auth routes are never a sensible post-auth destination — they'd loop. */
const AUTH_ROUTES = ["/login", "/signup", "/logout", "/forgot-password", "/auth/callback"];

/**
 * A `next` value is attacker-controlled (it comes off the URL), so it is only
 * honored when it is unambiguously a path on this site: one leading slash, and
 * no scheme or authority. `//evil.com` and `https://evil.com` are both browser
 * -navigable to another origin, so both are rejected.
 */
export function safeNext(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith("/")) return null;
  if (value.startsWith("//")) return null;
  if (value.includes("\\")) return null;
  const path = value.split("?")[0].split("#")[0];
  if (AUTH_ROUTES.includes(path)) return null;
  return value;
}

/** Read the post-auth destination out of a location's query string. */
export function nextFromSearch(search: string): string | null {
  return safeNext(new URLSearchParams(search).get(NEXT_PARAM));
}

/**
 * Build a link to an auth page that returns here afterwards.
 *
 * `from` is normally `location.pathname + location.search`. Passing the
 * current location through unchanged is deliberate: query state (a selected
 * window, a ticker, a `?unlock=` token) is part of where the visitor was.
 */
export function authUrl(base: string, from: string): string {
  const next = safeNext(from);
  if (!next) return base;
  return `${base}?${NEXT_PARAM}=${encodeURIComponent(next)}`;
}

/** `authUrl` for use where a react-router `To` is expected. */
export function authTo(base: string, from: string): To {
  return authUrl(base, from);
}

/**
 * `authUrl` bound to wherever the component currently is.
 *
 * Every gate in the app needs the same two lines (read the location, append it
 * as `next`); this keeps each of them to one call so a new gate is unlikely to
 * forget the return path. Memoized on the location so callers can list it as a
 * `useCallback`/`useEffect` dependency without re-running every render.
 */
export function useAuthUrl(): (base: string) => string {
  const location = useLocation();
  const from = `${location.pathname}${location.search}`;
  return useCallback((base: string) => authUrl(base, from), [from]);
}

/**
 * Carry an existing `?next=` across the login <-> signup <-> forgot-password
 * links, so switching forms doesn't silently discard the destination.
 */
export function withNext(base: string, search: string): string {
  const next = nextFromSearch(search);
  return next ? `${base}?${NEXT_PARAM}=${encodeURIComponent(next)}` : base;
}
